import { FC, useCallback, useEffect, useState } from 'react';
import { adminService } from '@/services/admin.service';
import type { AnalyticsDashboard, AnalyticsPeriod, AnalyticsSatisfactionComment } from '@/types/admin';
import KpiCard from '@/components/admin/KpiCard';
import BarChartCard from '@/components/admin/charts/BarChartCard';
import DonutChartCard from '@/components/admin/charts/DonutChartCard';
import FunnelCard from '@/components/admin/charts/FunnelCard';
import LineChartCard from '@/components/admin/charts/LineChartCard';
import RankTableCard from '@/components/admin/charts/RankTableCard';
import InfoTooltip from '@/components/InfoTooltip';

const PERIODS: { value: AnalyticsPeriod; label: string }[] = [
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: 'year', label: 'Este ano' },
  { value: 'all', label: 'Todo o período' },
];

const formatPct = (value: number | undefined): string => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
};

const formatHours = (value: number): string =>
  `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}h`;

const AdminReportsPage: FC = () => {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [data, setData] = useState<AnalyticsDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (p: AnalyticsPeriod) => {
    setIsLoading(true);
    setError(null);
    try {
      const dashboard = await adminService.getAnalytics(p);
      setData(dashboard);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Erro desconhecido';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(period);
  }, [period, fetchData]);

  const handleExportPdf = () => {
    const html = document.documentElement;
    const previousTheme = html.getAttribute('data-theme') ?? 'dark';
    // Força tema claro pra impressão (legibilidade + economia de tinta).
    // O useThemeColors observa data-theme via MutationObserver, então isso
    // dispara um re-render do Recharts com as cores claras.
    html.setAttribute('data-theme', 'light');

    const restore = () => {
      html.setAttribute('data-theme', previousTheme);
      window.removeEventListener('afterprint', restore);
    };
    window.addEventListener('afterprint', restore, { once: true });

    // Delay maior pra o ciclo completo acontecer antes do print:
    // 1) data-theme="light" no DOM → CSS vars recomputadas
    // 2) MutationObserver dispara → useThemeColors atualiza estado
    // 3) Recharts re-renderiza SVG com cores claras
    // 4) window.print() captura o resultado já tematizado
    window.setTimeout(() => window.print(), 300);
  };

  return (
    <div className="p-6 lg:p-10 report-printable">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-content-primary">Relatórios</h1>
          <p className="text-content-muted text-sm">
            Métricas de negócio da plataforma h.lab.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="print-hide flex flex-wrap gap-1 bg-surface-card border border-theme-border rounded-lg p-1">
            {PERIODS.map((p) => {
              const active = p.value === period;
              return (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPeriod(p.value)}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    active
                      ? 'bg-accent text-accent-contrast font-semibold'
                      : 'text-content-muted hover:text-content-primary'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={handleExportPdf}
            className="print-hide flex items-center gap-2 bg-accent text-accent-contrast px-4 py-2 rounded-lg text-sm font-semibold hover:bg-accent-hover transition-colors"
          >
            <span className="material-symbols-outlined text-base">download</span>
            Exportar PDF
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg border border-theme-danger/40 bg-theme-danger/10 text-content-primary flex items-center justify-between">
          <span>Erro ao carregar relatórios: {error}</span>
          <button
            type="button"
            onClick={() => fetchData(period)}
            className="text-sm underline hover:text-accent"
          >
            Tentar de novo
          </button>
        </div>
      )}

      {isLoading && !data && <ReportSkeleton />}

      {data && (
        <div className="flex flex-col gap-10">
          <AdoptionSection data={data} />
          <EngagementSection data={data} />
          <CompletionSection data={data} />
          <SectorsContentSection data={data} />
          <SatisfactionSection data={data} />
          <p className="text-xs text-content-muted text-right">
            Atualizado em {new Date(data.generated_at).toLocaleString('pt-BR')}
          </p>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Section 1 — Adoção & Cadastros
// =============================================================================

const AdoptionSection: FC<{ data: AnalyticsDashboard }> = ({ data }) => {
  const a = data.adoption;
  const levelData = a.level_distribution.map((d) => ({
    name: d.level_name || `Nível ${d.level}`,
    value: d.count,
  }));
  return (
    <section className="report-section flex flex-col gap-4">
      <SectionHeader title="Adoção & Cadastros" icon="group_add" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon="groups"
          label="Total de alunos"
          value={a.total_students}
          sublabel="cumulativo (ignora período)"
        />
        <KpiCard
          icon="bolt"
          label="Ativos no período"
          value={a.active_in_period}
        />
        <KpiCard
          icon="person_add"
          label="Novos no período"
          value={a.new_in_period}
        />
        <KpiCard
          icon="how_to_reg"
          label="Taxa de aprovação"
          value={formatPct(a.approval_rate)}
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LineChartCard
          title="Cadastros no tempo"
          subtitle="Novos usuários por bucket do período"
          data={a.signups_timeseries}
          dataKey="count"
        />
        <DonutChartCard
          title="Distribuição por nível"
          subtitle="Quantos alunos em cada nível de progressão"
          data={levelData}
        />
      </div>
    </section>
  );
};

// =============================================================================
// Section 2 — Engajamento & Atividade
// =============================================================================

const EngagementSection: FC<{ data: AnalyticsDashboard }> = ({ data }) => {
  const e = data.engagement;
  return (
    <section className="report-section flex flex-col gap-4">
      <SectionHeader title="Engajamento & Atividade" icon="local_fire_department" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon="menu_book" label="Aulas concluídas" value={e.lessons_completed} />
        <KpiCard icon="schedule" label="Horas de estudo" value={formatHours(e.total_study_hours)} />
        <KpiCard
          icon="whatshot"
          label="Streak médio"
          value={e.avg_streak.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
          sublabel="dias consecutivos"
        />
        <KpiCard icon="stars" label="XP distribuído" value={e.xp_distributed} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LineChartCard
          title="Aulas concluídas no tempo"
          data={e.activity_timeseries}
          dataKey="lessons"
        />
        <LineChartCard
          title="XP distribuído no tempo"
          data={e.xp_timeseries}
          dataKey="xp"
        />
      </div>
    </section>
  );
};

// =============================================================================
// Section 3 — Conclusão & Certificados
// =============================================================================

const CompletionSection: FC<{ data: AnalyticsDashboard }> = ({ data }) => {
  const c = data.completion;
  const funnelSteps = [
    { label: 'Inscritos', value: c.funnel.enrolled },
    { label: 'Em progresso', value: c.funnel.in_progress },
    { label: 'Concluído', value: c.funnel.completed },
    { label: 'Certificado emitido', value: c.funnel.certified },
  ];
  return (
    <section className="report-section flex flex-col gap-4">
      <SectionHeader title="Conclusão & Certificados" icon="workspace_premium" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon="task_alt"
          label="Trilhas concluídas"
          value={c.trails_completed}
          sublabel="no período"
        />
        <KpiCard
          icon="workspace_premium"
          label="Certificados emitidos"
          value={c.certificates_issued}
          sublabel="no período"
        />
        <KpiCard
          icon="trending_up"
          label="Conclusão média"
          value={formatPct(c.avg_completion_rate)}
          sublabel="média de progresso por trilha"
        />
        <KpiCard
          icon="quiz"
          label="Nota média de quiz"
          value={`${c.avg_quiz_score.toFixed(1)}%`}
          sublabel="no período"
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FunnelCard
          title="Funil de conversão"
          subtitle="Inscrito → Concluído → Certificado · totais cumulativos (não filtra por período)"
          steps={funnelSteps}
        />
        <BarChartCard
          title="Conclusões por trilha"
          subtitle={`Top ${c.completions_by_trail.length || 0} no período`}
          data={c.completions_by_trail.map((row) => ({
            trail: row.trail,
            count: row.count,
          }))}
          dataKey="count"
          categoryKey="trail"
          layout="vertical"
        />
      </div>
    </section>
  );
};

// =============================================================================
// Section 4 — Comparação por Setor & Desempenho de Conteúdo
// =============================================================================

const SectorsContentSection: FC<{ data: AnalyticsDashboard }> = ({ data }) => {
  const s = data.sectors_content;
  return (
    <section className="report-section flex flex-col gap-4">
      <SectionHeader title="Comparação por Setor & Desempenho de Conteúdo" icon="domain" />
      <BarChartCard
        title="Ranking de setores por XP"
        subtitle="Soma de XP dos membros ativos"
        data={s.sectors.map((row) => ({ sector: row.sector, total_xp: row.total_xp }))}
        dataKey="total_xp"
        categoryKey="sector"
        layout="vertical"
        height={Math.max(280, 40 * s.sectors.length)}
        colorByIndex
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RankTableCard
          title="Cursos mais populares"
          subtitle="Top 10 por inscrição"
          rows={s.top_courses}
          columns={[
            { key: 'trail', label: 'Curso' },
            { key: 'enrolled', label: 'Inscritos', align: 'right' },
            { key: 'completed', label: 'Concluídos', align: 'right' },
            {
              key: 'completion_rate',
              label: 'Taxa',
              align: 'right',
              render: (row) => formatPct(row.completion_rate ?? 0),
            },
          ]}
        />
        <RankTableCard
          title="Cursos com maior abandono"
          subtitle="Top 5 (apenas trilhas com ≥5 inscritos)"
          rows={s.high_dropout_courses}
          columns={[
            { key: 'trail', label: 'Curso' },
            { key: 'enrolled', label: 'Inscritos', align: 'right' },
            { key: 'completed', label: 'Concluídos', align: 'right' },
            {
              key: 'dropout_rate',
              label: 'Abandono',
              align: 'right',
              render: (row) => formatPct(row.dropout_rate ?? 0),
            },
          ]}
        />
      </div>
    </section>
  );
};

// =============================================================================
// Section 5 — Satisfação dos Alunos (NPS)
// =============================================================================

const npsColor = (nps: number): string => {
  if (nps >= 50) return 'text-emerald-500';
  if (nps >= 0) return 'text-amber-500';
  return 'text-rose-500';
};

const SatisfactionSection: FC<{ data: AnalyticsDashboard }> = ({ data }) => {
  const s = data.satisfaction;
  const empty = s.response_count === 0;

  return (
    <section className="report-section flex flex-col gap-4" data-testid="satisfaction-section">
      <SectionHeader title="Satisfação dos Alunos" icon="sentiment_satisfied" tooltipKey="nps" />

      {empty ? (
        <div className="rounded-xl border border-theme-border bg-surface-card p-8 text-center">
          <span className="material-symbols-outlined text-4xl text-content-muted mb-2">
            inbox
          </span>
          <p className="text-content-muted">
            Ainda não há respostas de pesquisa neste período.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <KpiCard
              icon="trending_up"
              label="NPS geral"
              value={
                <span className={npsColor(s.nps_overall)}>
                  {s.nps_overall > 0 ? `+${s.nps_overall}` : s.nps_overall}
                </span>
              }
              sublabel="-100 a +100"
            />
            <KpiCard
              icon="sentiment_satisfied"
              label="CSAT médio"
              value={s.csat_avg.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
              sublabel="0 a 10"
            />
            <KpiCard
              icon="how_to_vote"
              label="Respostas no período"
              value={s.response_count}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DonutChartCard
              title="Distribuição NPS"
              subtitle="Detratores (0-6), neutros (7-8), promotores (9-10)"
              data={[
                { name: 'Promotores', value: s.distribution.promoters },
                { name: 'Neutros', value: s.distribution.passives },
                { name: 'Detratores', value: s.distribution.detractors },
              ]}
            />
            <RankTableCard
              title="NPS por curso"
              subtitle={`${s.per_course.length} cursos com pelo menos 1 resposta`}
              rows={s.per_course}
              columns={[
                { key: 'trail', label: 'Curso' },
                { key: 'response_count', label: 'Respostas', align: 'right' },
                {
                  key: 'nps',
                  label: 'NPS',
                  align: 'right',
                  render: (row) => (
                    <span className={npsColor(row.nps)}>
                      {row.nps > 0 ? `+${row.nps}` : row.nps}
                    </span>
                  ),
                },
                {
                  key: 'csat_avg',
                  label: 'CSAT',
                  align: 'right',
                  render: (row) =>
                    row.csat_avg.toLocaleString('pt-BR', { maximumFractionDigits: 1 }),
                },
              ]}
            />
          </div>

          <CommentsList comments={s.comments} />
        </>
      )}
    </section>
  );
};

const CommentsList: FC<{ comments: AnalyticsSatisfactionComment[] }> = ({ comments }) => {
  if (comments.length === 0) {
    return (
      <div className="rounded-xl border border-theme-border bg-surface-card p-6">
        <h3 className="text-sm font-semibold text-content-primary uppercase tracking-wide mb-2">
          Comentários
        </h3>
        <p className="text-sm text-content-muted">Sem comentários no período.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-theme-border bg-surface-card p-6">
      <h3 className="text-sm font-semibold text-content-primary uppercase tracking-wide mb-4">
        Comentários ({comments.length})
      </h3>
      <ul className="flex flex-col gap-3">
        {comments.map((c, idx) => (
          <li
            key={`${c.trail}-${c.created_at}-${idx}`}
            className="border-l-2 border-accent/40 pl-4 py-1"
          >
            <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted mb-1">
              <span className="font-semibold text-content-secondary">{c.trail}</span>
              <span>·</span>
              <span>CSAT {c.csat_score}</span>
              <span>·</span>
              <span>NPS {c.nps_score}</span>
              <span>·</span>
              <span>{new Date(c.created_at).toLocaleDateString('pt-BR')}</span>
            </div>
            <p className="text-sm text-content-primary leading-snug">{c.comment}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

// =============================================================================
// Helpers
// =============================================================================

const SectionHeader: FC<{ title: string; icon: string; tooltipKey?: 'nps' | 'csat' }> = ({
  title,
  icon,
  tooltipKey,
}) => (
  <div className="flex items-center gap-2 border-b border-theme-border pb-2">
    <span className="material-symbols-outlined text-accent">{icon}</span>
    <h2 className="text-lg font-semibold uppercase tracking-wide text-content-primary">
      {title}
    </h2>
    {tooltipKey && <InfoTooltip termKey={tooltipKey} iconOnly />}
  </div>
);

const ReportSkeleton: FC = () => (
  <div className="flex flex-col gap-8">
    {[1, 2, 3, 4].map((s) => (
      <div key={s} className="flex flex-col gap-3">
        <div className="h-6 w-64 bg-surface-card rounded-md animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((k) => (
            <div
              key={k}
              className="h-28 bg-surface-card border border-theme-border rounded-xl animate-pulse"
            />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2].map((k) => (
            <div
              key={k}
              className="h-64 bg-surface-card border border-theme-border rounded-xl animate-pulse"
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default AdminReportsPage;
